import axios from "axios";
import { Observable } from "rxjs";
import { ObjType } from "../../index";
import { NotImplementedError, UnauthenticatedError } from "../../errors";
import { booleanExpressionToServiceBooleanExpression, orgRecipePreferenceToServiceOrgRecipePreference, recipeToServiceRecipeBase, RecipeType, serviceOrgRecipePreferenceToOrgRecipePreference, serviceRecipeToRecipe } from "../../models/recipe";
import { FilterTypes, FilterValueTypes, parseIdFilter, validateFilterStructure } from "../../handlers/filter";
import { get, set } from "idb-keyval";
import { ObservableHandler } from "../../handlers/observable";
import { RecipeMatchFailed, RecipeSaveFailed, RecipeFetchFailed, RecipePatchFailed } from "./errors";
import { toQueryParams } from "./filters";
import { MINIMAL_RECIPE_FILTERS, DEFAULT_RECIPE_FILTERS } from "./constants";
// if audiences structure changes, update this to invalidate the cache
const RECIPES_CACHE_VERSION = 1;
/**
 * Recipes module for managing recipes with configurable persistent caching.
 *
 * When the 'enable_madsdk_persistent_caching' feature flag is enabled, mutations
 * surgically update the persistent cache rather than clearing it entirely.
 *
 * When disabled, mutations clear all resource caches (default cache behavior).
 */
class Recipes extends ObservableHandler {
    constructor(sdk) {
        const recipesUrl = `${sdk.urls.baseAPIUrl}/recipes`;
        const PERSISTENT_CACHE_URLS = [
            `${recipesUrl}${toQueryParams(MINIMAL_RECIPE_FILTERS)}`,
            `${recipesUrl}${toQueryParams(DEFAULT_RECIPE_FILTERS)}`
        ];
        super(sdk, "recipes", { atomize: true, persistResources: PERSISTENT_CACHE_URLS });
        this.loadRecipesCache = async () => {
            const userId = this.sdk.getCurrentUser()?.id;
            const cache = await get(`recipes-${userId}`);
            if (!cache || cache.version !== RECIPES_CACHE_VERSION) {
                return null;
            }
            return cache.data;
        };
        this.saveRecipesCache = async (recipes) => {
            const userId = this.sdk.getCurrentUser()?.id;
            await set(`recipes-${userId}`, {
                version: RECIPES_CACHE_VERSION,
                data: recipes
            });
        };
        this.url = {
            single: `${this.sdk.urls.baseAPIUrl}/recipe`,
            multiple: recipesUrl,
            match: `${this.sdk.urls.baseAPIUrl}/recipebyexpr`,
            preference: `${this.sdk.urls.baseAPIUrl}/recipe/preferences`
        };
    }
    findItems(filters) {
        return new Observable((subscriber) => {
            try {
                validateFilterStructure(filters, [
                    { filterType: FilterTypes.IN, valueType: FilterValueTypes.OBJECT },
                    { filterType: FilterTypes.EQ, valueType: FilterValueTypes.STRING },
                    { filterType: FilterTypes.EQ, valueType: FilterValueTypes.BOOLEAN }
                ]);
            }
            catch (error) {
                subscriber.error(error);
                return;
            }
            const idFilter = parseIdFilter(filters);
            const promise = idFilter.size === 1
                ? this.getRecipe(idFilter.values().next().value, filters).then((recipe) => [recipe])
                : this.getRecipes(idFilter, filters);
            promise
                .then((recipes) => {
                subscriber.next(recipes);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    /**
     * Get multiple recipes.
     * @param ids: ids to filter as included. If empty, returns all recipes. Otherwise, only those with IDs in the set.
     * @param filters: optional filters to apply (e.g., loadBoolExpr, fetchPreferred).
     * @return: the array of gotten recipes.
     */
    async getRecipes(ids, filters) {
        const queryParams = toQueryParams(filters);
        const url = `${this.url.multiple}${queryParams}`;
        const all = await this.cache.promise(url, () => axios
            .get(url, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            return res?.data?.map((recipe) => this.toRecipe(recipe)) || [];
        })
            .catch((error) => {
            throw new RecipeFetchFailed(error);
        }));
        if (ids.size === 0) {
            await this.saveRecipesCache(all);
            return all;
        }
        return all.filter((recipe) => ids.has(recipe.id));
    }
    /**
     * Get a single recipe.
     * @param id: the recipe's id.
     * @param filters: optional filters to apply (e.g., loadBoolExpr, fetchPreferred).
     * @return: the recipe.
     */
    getRecipe(id, filters) {
        const queryParams = toQueryParams(filters);
        const url = `${this.url.single}/${id}${queryParams}`;
        return this.cache.promise(url, () => axios
            .get(url, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((res) => {
            return serviceRecipeToRecipe(res.data.data);
        })
            .catch((error) => {
            throw new RecipeFetchFailed(error);
        }));
    }
    /**
     * @deprecated
     * recipes does not support make.
     */
    make() {
        return new Promise((_, reject) => {
            reject(new NotImplementedError("make"));
        });
    }
    saveItem(toSave) {
        return new Observable((subscriber) => {
            const user = this.sdk.getCurrentUser();
            if (typeof user === "undefined") {
                throw new UnauthenticatedError();
            }
            this.normalizeRecipesForSaving(Array.isArray(toSave) ? toSave : [toSave]).then((normalized) => {
                let containsStandardRecipe = false;
                const preferences = [];
                const recipeToOrgPreferred = new Map();
                for (const recipe of normalized) {
                    preferences.push({
                        orgId: user.parent || "",
                        recipeId: recipe.id,
                        preferred: !!recipe.preferred
                    });
                    recipeToOrgPreferred.set(recipe.id, {
                        orgId: user.parent || "",
                        preferred: !!recipe.preferred
                    });
                    if (recipe.type === RecipeType.STANDARD) {
                        containsStandardRecipe = true;
                    }
                }
                // For standard recipes, we don't save them to the backend (they are read-only templates).
                // We only update the org-specific preference.
                // For custom recipes, we save them to the backend first, then update the preference.
                const recipesPromise = containsStandardRecipe
                    ? Promise.resolve(normalized.map((recipe) => ({
                        ...recipe,
                        preferred: recipeToOrgPreferred.get(recipe.id)?.preferred
                    })))
                    : this.saveRecipes(normalized);
                recipesPromise
                    .then((recipesToReturn) => {
                    // Set preferred field on all returned recipes
                    for (const recipe of recipesToReturn) {
                        recipe.preferred = recipeToOrgPreferred.get(recipe.id)?.preferred;
                    }
                    // Return result to subscriber
                    if (recipesToReturn.length === 1) {
                        subscriber.next(recipesToReturn[0]);
                    }
                    else {
                        subscriber.next(recipesToReturn);
                    }
                    // Save org-specific preferences
                    axios
                        .post(this.url.preference, preferences.map((preference) => this.fromOrgRecipePreference(preference)), {
                        headers: {
                            "Content-Type": "application/json"
                        }
                    })
                        .then(() => {
                        this.cache.reconcile("save", recipesToReturn);
                        subscriber.complete();
                    })
                        .catch((error) => {
                        subscriber.error(error);
                    });
                })
                    .catch((error) => {
                    subscriber.error(error);
                });
            });
        });
    }
    /**
     * Normalizes recipe fields for backend compatibility.
     * Currently handles: replacing em/en dashes with hyphens in name field.
     * @param recipe: the recipe to normalize
     * @return: normalized recipe
     */
    normalizeRecipeFields(recipe) {
        // en and em dashes aren't supported by backend, so replace with hyphen
        if (recipe.name) {
            return {
                ...recipe,
                name: recipe.name.replace(/\u2013|\u2014/g, "-")
            };
        }
        return recipe;
    }
    /**
     * Converts recipes to service format with all necessary transformations.
     * Applies field normalization and converts to ServiceRecipeBase format.
     * @param recipes: recipes to convert
     * @return: array of service recipe payloads
     */
    toServiceRecipes(recipes) {
        return recipes.map((recipe) => {
            const normalized = this.normalizeRecipeFields(recipe);
            return this.fromRecipe(normalized);
        });
    }
    /**
     * Ensures all the recipes have a valid id, and properly set their parentage for saving.
     * @param recipes: the recipe to normalize.
     * @return: the normalized recipe set.
     */
    normalizeRecipesForSaving(recipes) {
        const promises = [];
        const user = this.sdk.getCurrentUser();
        if (typeof user === "undefined") {
            throw new UnauthenticatedError();
        }
        recipes.forEach((recipe) => {
            let normalized;
            if (recipe.id) {
                normalized = Promise.resolve({
                    ...recipe,
                    id: recipe.id
                });
            }
            else {
                // if no id, we're creating a recipe
                normalized = this.sdk.cryptography.mintKey(ObjType.SEG).then((id) => ({
                    ...recipe,
                    id
                }));
            }
            promises.push(normalized);
        });
        return Promise.all(promises);
    }
    /**
     * POSTs the proper endpoint with the given recipe.
     * @param recipe: the recipe to save.
     * @return: the saved recipe.
     */
    saveRecipes(recipes) {
        return new Promise((resolve, reject) => {
            axios
                .post(this.url.multiple, this.toServiceRecipes(recipes), {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(({ data: res }) => {
                const saved = Array.isArray(res.data)
                    ? res.data.map((recipe) => this.toRecipe(recipe))
                    : [this.toRecipe(res.data)];
                resolve(saved);
            })
                .catch((error) => {
                reject(new RecipeSaveFailed(error));
            });
        });
    }
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    async deleteItem(id) {
        return new Promise((_, reject) => {
            reject(new NotImplementedError("delete"));
        });
    }
    /**
     * Checks to see if a recipe with an equivalent expression already exists.
     * @param toMatch: a recipe or an expression to check.
     * @return: if an already-existing recipe matches the provided one, that recipe is returned. Else, null.
     */
    match(toMatch) {
        return new Observable((subscriber) => {
            const expression = "name" in toMatch && "type" in toMatch
                ? this.fromRecipe(toMatch).expr
                : booleanExpressionToServiceBooleanExpression(toMatch);
            axios
                .post(this.url.match, expression, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then((result) => {
                if (result.data.data) {
                    subscriber.next(this.toRecipe(result.data.data));
                }
                else {
                    subscriber.next(null);
                }
            })
                .catch((error) => {
                subscriber.error(new RecipeMatchFailed(error));
            })
                .finally(() => {
                subscriber.complete();
            });
        });
    }
    /**
     * Converts from a service recipe to a client recipe.
     * @param recipe: the recipe to convert.
     * @return: the converted recipe.
     */
    toRecipe(recipe) {
        return serviceRecipeToRecipe(recipe);
    }
    /**
     * Converts from a client recipe to a service recipe.
     * @param recipe: the recipe to convert (can be partial or full)
     * @return: service recipe payload with only provided fields
     */
    fromRecipe(recipe) {
        return recipeToServiceRecipeBase(recipe);
    }
    /**
     * Converts from a service org recipe preference to a client org recipe preference.
     * @param orgRecipePreference: the org recipe preference to convert.
     * @return: the converted org recipe preference.
     */
    toOrgRecipePreference(orgRecipePreference) {
        return serviceOrgRecipePreferenceToOrgRecipePreference(orgRecipePreference);
    }
    /**
     * Converts from a client org recipe preference to a service org recipe preference.
     * @param orgRecipePreference: the org recipe preference to convert.
     * @return: the converted org recipe preference.
     */
    fromOrgRecipePreference(orgRecipePreference) {
        return orgRecipePreferenceToServiceOrgRecipePreference(orgRecipePreference);
    }
    /**
     * Updates a recipe's preference status without saving the recipe itself.
     * @param recipe: the recipe to update preference for
     * @param preferred: the new preferred status (true or false)
     * @return: Promise that resolves with the updated recipe
     */
    async updatePreference(recipe, preferred) {
        const user = this.sdk.getCurrentUser();
        if (!user?.parent) {
            throw new UnauthenticatedError();
        }
        const preference = {
            orgId: user.parent,
            recipeId: recipe.id,
            preferred: preferred
        };
        return axios
            .post(this.url.preference, [this.fromOrgRecipePreference(preference)], {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(() => {
            // Update the recipe with the new preference
            const updatedRecipe = { ...recipe, preferred };
            this.reconcileCache("save", updatedRecipe);
            return updatedRecipe;
        });
    }
    /**
     * Patches specific fields of one or more recipes without requiring full recipe data.
     * @param patches - Array of partial recipe updates (must include id)
     * @return Promise that resolves with array of updated recipes
     */
    async patch(patches) {
        const user = this.sdk.getCurrentUser();
        if (!user?.parent) {
            throw new UnauthenticatedError();
        }
        const servicePatchPayloads = this.toServiceRecipes(patches);
        try {
            const response = await axios.patch(this.url.multiple, { data: servicePatchPayloads }, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            // Convert response back to Recipe format
            const updatedRecipes = Array.isArray(response.data.data)
                ? response.data.data.map((recipe) => this.toRecipe(recipe))
                : [this.toRecipe(response.data.data)];
            // Update cache with updated recipes
            this.cache.reconcile("save", updatedRecipes);
            return updatedRecipes;
        }
        catch (error) {
            throw new RecipePatchFailed(error);
        }
    }
}
export default Recipes;
export * from "./types";
//# sourceMappingURL=index.js.map